import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IMethod } from '../../../stores/model/metamodel/method';

/**
 * Check the consistency of an inherited method.
 *
 * Checks that:
 * - the method has a viaInheritance
 * - the method has a viaMethod
 * - the types of the method and its viaMethod are consistent
 * - the provenance is set to INHERITED
 */
class InheritedMethodConsistencyCheck extends Check<IMethod> {
  static description = 'Check consistency of inherited method';

  check(): Report {
    this.report = new Report();

    // Check if there is a viaInheritance
    if (this.element.viaInheritance === null) {
      this.report.addMessage(
        MessageType.ERROR,
        `Inherited method (${this.element.asString()}) has no originating inheritance.`,
        this,
        this.element
      );
    } else {
      // There is a via inheritance
      this.report.addMessage(
        MessageType.INFORMATION,
        `Inherited method (${this.element.asString()}) has an originating inheritance.`,
        this,
        this.element
      );
    }

    // Check if there is a via method
    if (this.element.viaMethod === null) {
      // Oops, there is no via method
      this.report.addMessage(
        MessageType.ERROR,
        `Inherited method (${this.element.asString()}) has no originating method.`,
        this,
        this.element
      );
    } else {
      // There is a via method
      this.report.addMessage(
        MessageType.INFORMATION,
        `Inherited method (${this.element.asString()}) has originating method.`,
        this,
        this.element
      );

      // Now check whether the type is consistent with the type of the via
      // method
      const viaType = this.element.viaMethod.type;
      const type = this.element.type;
      if (type !== viaType) {
        this.report.addMessage(
          MessageType.ERROR,
          "The inherited method type is not set to the type of it's originating method.",
          this,
          this.element
        );
      } else {
        this.report.addMessage(
          MessageType.INFORMATION,
          'The inherited method has a valid type.',
          this,
          this.element
        );
      }
    }

    // Finally, check whether the provenance is set to INHERITED.
    if (this.element.provenance !== 'INHERITED') {
      this.report.addMessage(
        MessageType.ERROR,
        `Inherited method (${this.element.asString()}) does not have its provenance set to INHERITED.`,
        this,
        this.element
      );
    } else {
      this.report.addMessage(
        MessageType.INFORMATION,
        `Inherited method (${this.element.asString()}) provenance is set correctly.`,
        this,
        this.element
      );
    }

    return this.report;
  }
}

export default InheritedMethodConsistencyCheck;
